import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import GemIcon from '../../components/Idle/GemIcon';
import { formatCoins } from '../../lib/idle/format';
import { fetchBoard, nextReset, rewardForRank } from '../../lib/idle/weekly';
import Styles from './Leaderboard.module.css';
import PrizesModal from './PrizesModal';

const REFRESH_MS = 60000;

const countdown = (ms) => {
  if (ms <= 0) return 'any moment now';
  const days = Math.floor(ms / 86400000);
  const hours = Math.floor((ms % 86400000) / 3600000);
  const minutes = Math.floor((ms % 3600000) / 60000);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${Math.max(1, minutes)}m`;
};

const resetLabel = () =>
  new Date(nextReset()).toLocaleString(undefined, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

const errorText = (error) => {
  if (error && error.code === 'permission-denied') {
    return "Your Firestore rules don't allow reading the leaderboard.";
  }
  return `The leaderboard didn't load${error && error.code ? ` (${error.code})` : ''}. Try again soon.`;
};

const Row = ({ row, mine }) => {
  const prize = row.rank ? rewardForRank(row.rank) : 0;
  const medal = row.rank && row.rank <= 3 ? Styles[`medal${row.rank}`] : '';

  return (
    <li className={`${Styles.row} ${mine ? Styles.rowMine : ''}`}>
      <span className={`${Styles.rank} ${medal}`}>
        {row.rank || '–'}
      </span>

      <span className={Styles.name}>
        <span className={Styles.nameText}>
          {row.username}
          {mine && <em> (you)</em>}
        </span>

        {row.premium && (
          <span className={Styles.tag}>Premium</span>
        )}
      </span>

      <span className={Styles.solved}>
        {formatCoins(row.solved)}
      </span>

      <span
        className={`${Styles.prize} ${prize ? '' : Styles.noPrize}`}
        title="Prize at reset"
      >
        {prize ? (
          <>
            <GemIcon size={18} />
            {prize}
          </>
        ) : null}
      </span>
    </li>
  );
};

const Leaderboard = () => {
  const [board, setBoard] = useState(null);
  const [error, setError] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [showPrizes, setShowPrizes] = useState(false);

  const load = useCallback(async () => {
    try {
      setBoard(await fetchBoard());
      setError(null);
    } catch (err) {
      console.error('Could not load the leaderboard', err);
      setError(err || new Error('unknown'));
    }
  }, []);

  // Loads on open, when sign-in finishes, and once a minute after.
  useEffect(() => {
    load();
    const stopAuth = onAuthStateChanged(getAuth(), load);
    const refresh = setInterval(load, REFRESH_MS);
    const tick = setInterval(() => setNow(Date.now()), 30000);
    return () => {
      stopAuth();
      clearInterval(refresh);
      clearInterval(tick);
    };
  }, [load]);

  const me = board && board.me;
  const meInTop = Boolean(me && board.top.some((row) => row.id === me.id));

  return (
    <main className={Styles.page}>
      <div className={Styles.column}>
        <h1 className={Styles.title}>Weekly Leaderboard</h1>
        <p className={Styles.sub}>Most flashcards solved this week</p>

        <div className={Styles.bar}>
          <span className={Styles.reset}>
            Resets in <strong>{countdown(nextReset(now) - now)}</strong>
            <span> · {resetLabel()}</span>
          </span>
          <button type="button" className={Styles.prizesBtn} onClick={() => setShowPrizes(true)}>
            <GemIcon size={18} /> Prizes
          </button>
        </div>

        <section className={Styles.sheet}>
          {error && !board && <p className={Styles.note}>{errorText(error)}</p>}
          {!board && !error && <p className={Styles.note}>Loading…</p>}

          {board && (
            <>
              <ol className={Styles.list}>
                {board.top.length === 0 ? (
                  <li className={Styles.empty}>Nobody has solved a card yet this week. Be first!</li>
                ) : (
                  board.top.map((row) => (
                    <Row key={row.id} row={row} mine={meInTop && row.id === me.id} />
                  ))
                )}
              </ol>

              {/* Below the top 20: your own place, under the list */}
              {board.signedIn && me && !meInTop && (
                <div className={Styles.pinned}>
                  <ol className={Styles.list}>
                    <Row row={me} mine />
                  </ol>
                  {!me.rank && <p className={Styles.hint}>Solve a flashcard to get on the board.</p>}
                </div>
              )}
              {!board.signedIn && (
                <p className={Styles.hint}>
                  <Link to="/signup">Create an account</Link> to join the leaderboard.
                </p>
              )}
            </>
          )}
        </section>
      </div>

      {showPrizes && <PrizesModal onClose={() => setShowPrizes(false)} />}
    </main>
  );
};

export default Leaderboard;